<script setup>
import { computed, watch, toRef } from 'vue';

const props = defineProps({
  value: {
    type: String,
    default: ''
  },
  fieldType: {
    type: String,
    default: '',
    validator: (value) => ['agentName', 'agentRole', 'companyDescription', 'imagePrompt', ''].includes(value)
  },
  error: {
    type: String,
    default: ''
  },
  touched: {
    type: Boolean,
    default: false
  },
  showHelper: {
    type: Boolean,
    default: true
  },
  helperText: {
    type: String,
    default: ''
  },
  showCharCount: {
    type: Boolean,
    default: false
  },
  currentLength: {
    type: Number,
    default: 0
  },
  maxLength: {
    type: Number,
    default: 0
  },
  minLength: {
    type: Number,
    default: 0
  },
  isImagePromptEnabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['validation']);

// Regex patterns
const nameRegex = /^[\p{L}\p{N} ._-]+$/u;
const roleRegex = /^[\p{L}\p{N} \n,.-]+$/u;
const descriptionRegex = /^[\p{L}\p{N} \n]+$/u;

// Validation functions
const validateAgentName = (name) => {
  const trimmedName = name.trim();
  
  if (!trimmedName) {
    return { isValid: false, error: 'O nome do agente é obrigatório' };
  }
  
  if (trimmedName.length < 3 || trimmedName.length > 50) {
    return { isValid: false, error: 'O nome deve ter entre 3 e 50 caracteres' };
  }
  
  if (!nameRegex.test(name)) {
    return { isValid: false, error: 'Use apenas letras (com ou sem acento), números, espaço, ponto (.), hífen (-) ou sublinhado (_)' };
  }
  
  if (/^[ ._-]|[ ._-]$/.test(name)) {
    return { isValid: false, error: 'O nome não pode começar ou terminar com espaço, ponto, hífen ou sublinhado' };
  }
  
  return { isValid: true, error: '' };
};

const validateAgentRole = (role) => {
  const trimmedRole = role.trim();
  
  if (!trimmedRole) {
    return { isValid: false, error: 'A função do agente é obrigatória' };
  }
  
  if (trimmedRole.length < 150 || trimmedRole.length > 300) {
    return { isValid: false, error: 'A função deve ter entre 150 e 300 caracteres' };
  }
  
  if (!roleRegex.test(role)) {
    return { isValid: false, error: 'Use apenas letras (com ou sem acento), números, espaços, vírgulas, pontos, hífens e quebras de linha' };
  }
  
  if (/^[ \n]|[ \n]$/.test(role)) {
    return { isValid: false, error: 'A função não pode começar ou terminar com espaço ou quebra de linha' };
  }
  
  return { isValid: true, error: '' };
};

const validateCompanyDescription = (description) => {
  const trimmedDescription = description.trim();
  
  if (!trimmedDescription) {
    return { isValid: false, error: 'A descrição da empresa é obrigatória' };
  }
  
  if (trimmedDescription.length < 250 || trimmedDescription.length > 2000) {
    return { isValid: false, error: 'A descrição deve ter entre 250 e 2000 caracteres' };
  }
  
  // Verifica apenas caracteres não permitidos (< e >)
  if (description.includes('<') || description.includes('>')) {
    return { isValid: false, error: 'Os caracteres < e > não são permitidos' };
  }
  
  if (!descriptionRegex.test(description)) {
    return { isValid: false, error: 'Use apenas letras (com ou sem acento), números, espaços e quebras de linha' };
  }
  
  if (/^[ \n]|[ \n]$/.test(description)) {
    return { isValid: false, error: 'A descrição não pode começar ou terminar com espaço ou quebra de linha' };
  }
  
  return { isValid: true, error: '' };
};

const validateImagePrompt = (prompt, isEnabled = false) => {
  // Se as instruções customizadas estão habilitadas, o campo é obrigatório
  if (isEnabled) {
    if (!prompt || prompt.trim() === '') {
      return { isValid: false, error: 'As instruções de imagem são obrigatórias quando o campo está habilitado' };
    }
    
    if (prompt.trim().length < 10) {
      return { isValid: false, error: 'As instruções devem ter pelo menos 10 caracteres' };
    }
  }
  
  // Verificar se não excede o limite de 2000 caracteres
  if (prompt && prompt.length > 2000) {
    return { isValid: false, error: 'As instruções não podem exceder 2000 caracteres' };
  }
  
  return { isValid: true, error: '' };
};

// Computed validation error
const validationError = computed(() => {
  
  if (!props.fieldType) return '';
  
  let result = { isValid: true, error: '' };
  
  if (props.fieldType === 'agentName') {
    result = validateAgentName(props.value);
  } else if (props.fieldType === 'agentRole') {
    result = validateAgentRole(props.value);
  } else if (props.fieldType === 'companyDescription') {
    result = validateCompanyDescription(props.value);
  } else if (props.fieldType === 'imagePrompt') {
    result = validateImagePrompt(props.value, props.isImagePromptEnabled);
  }
  
  return result.error;
});

// Watch for validation changes
watch([() => props.value, () => props.fieldType, () => props.touched, () => props.isImagePromptEnabled], () => {
  if (props.fieldType) {
    let result = { isValid: true };
    
    if (props.fieldType === 'agentName') {
      result = validateAgentName(props.value);
    } else if (props.fieldType === 'agentRole') {
      result = validateAgentRole(props.value);
    } else if (props.fieldType === 'companyDescription') {
      result = validateCompanyDescription(props.value);
    } else if (props.fieldType === 'imagePrompt') {
      result = validateImagePrompt(props.value, props.isImagePromptEnabled);
    }
    
    emit('validation', result.isValid);
  }
}, { immediate: true });

// Final error to display (props.error takes precedence)
const displayError = computed(() => {
  const error = props.error || validationError.value;
  return error;
});

const showError = computed(() => {
  // Mostra erro quando há erro E o campo foi tocado OU quando há valor no campo
  const hasError = !!displayError.value;
  const hasValue = props.value && props.value.trim().length > 0;
  const show = hasError && (props.touched || hasValue);
  return show;
});

const remainingChars = computed(() => {
  if (!props.maxLength) return 0;
  return props.maxLength - props.currentLength;
});

const charCountClass = computed(() => {
  if (props.minLength && props.currentLength < props.minLength) {
    return 'text-n-ruby-9';
  }
  return 'text-n-slate-10';
});
</script>

<template>
  <div class="min-h-[1.25rem] text-sm">
    <!-- Error message -->
    <Transition
      name="feedback"
      mode="out-in"
    >
      <span 
        v-if="showError" 
        class="text-n-ruby-9 flex items-center gap-1"
        key="error"
      >
        <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ displayError }}
      </span>
      
      <!-- Helper text -->
      <span 
        v-else-if="showHelper && helperText" 
        class="text-n-slate-11"
        key="helper"
      >
        {{ helperText }}
      </span>
    </Transition>
  </div>
</template>

<style scoped>
/* Transition for smooth feedback changes */
.feedback-enter-active,
.feedback-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.feedback-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}

.feedback-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>